/**
 * Offline-aware ``window.fetch`` wrapper.
 *
 * Intercepts writes (POST/PATCH/PUT/DELETE) to annotation +
 * draft endpoints. When the request fails because the device is
 * offline (or the network throws transiently), the request is
 * stored in the IndexedDB outbox (``offline-outbox.ts``) and the
 * caller receives an optimistic ``202 Accepted`` synthetic
 * Response so it doesn't error up the React tree.
 *
 * On reconnect, the sync runner drains the queue against the
 * UNWRAPPED fetch, replaying each request in FIFO order. The
 * cloud's ``unique_id`` idempotency guarantees that a replayed
 * write that the cloud already accepted (rare race during a
 * brief drop) is silently de-duped — same property desktop's
 * ``CloudProxyMiddleware`` relies on.
 *
 * Scope: only active when ``body.is-capacitor`` is set (mobile
 * bundle). Web + desktop wrap is a no-op so the existing flows
 * are unchanged.
 *
 * Future steps (5b-e) layer on top:
 *   - 5b: read-side cache (task data falls back to IDB when
 *     offline)
 *   - 5d: ``Download for offline`` pre-fetches task batches into
 *     the IDB read store + the SW media cache
 *   - 5e: backoff + conflict policy + ``X pending`` indicator in
 *     the menubar
 */
import {
  enqueue,
  list,
  remove,
  update,
  count,
  type OutboxEntry,
  deadLetter,
  getRecord,
  getAllByIndex,
  getAllRecords,
  putRecord,
  deleteRecord,
  STORE_PROJECTS,
  STORE_TASKS,
  STORE_ANNOTATIONS,
  STORE_DRAFTS,
  STORE_API_CACHE,
  STORE_URL_CONTENT,
} from "./offline-outbox";
import { isCapacitor } from "./host";

/** Endpoints whose successful responses we persist to IDB so they
 *  survive an app reload while offline. Keyed by exact pathname.
 *  Used for cloud-first reads (whoami, organizations) that have no
 *  per-resource IDB store of their own. */
const PERSISTED_API_PATHS = new Set([
  "/api/current-user/whoami",
  "/api/current-user/whoami/",
  "/api/organizations",
  "/api/organizations/",
  // Notifications: replay the last-seen unread count + list on a flaky
  // link instead of stubbing to empty — the bell going blank on every
  // hiccup reads as "no notifications" and hides real ones.
  "/api/current-user/notifications/unread-count",
  "/api/current-user/notifications/unread-count/",
]);

/** Endpoints whose responses we persist keyed by ``path?query``
 *  rather than just the path. Used when the same endpoint is asked
 *  with different query params (e.g. ``/api/dm/columns?project=6``
 *  vs ``project=7``) — caching by path alone would let the wrong
 *  project's columns leak across cached projects. */
const PERSISTED_API_PATHS_WITH_QUERY = new Set([
  "/api/dm/columns",
  "/api/dm/columns/",
  "/api/current-user/notifications",
  "/api/current-user/notifications/",
  // Discover feed + the user's joined set: cached per query string so
  // the default feed, a search, and each pagination page each replay
  // as last seen. Offline behaviour per CROWDSOURCE_ARCHITECTURE.md —
  // the last-seen catalog renders from cache; Join itself stays
  // online-only (the POST is not queueable and the UI disables it).
  "/api/discover",
  "/api/discover/",
  "/api/discover/mine",
  "/api/discover/mine/",
]);

const cacheKeyForUrl = (url: string): string | null => {
  let path = url;
  let queryStr = "";
  try {
    if (url.startsWith("http")) {
      const u = new URL(url);
      path = u.pathname;
      queryStr = u.search;
    } else {
      const q = url.indexOf("?");
      if (q !== -1) {
        queryStr = url.slice(q);
        path = url.slice(0, q);
      }
    }
  } catch (_) {
    return null;
  }
  // Normalize trailing slashes — Django's URL routing accepts both
  // /api/current-user/whoami and .../whoami/, but the cache key must
  // be canonical so a write at one form and a read at the other line
  // up. Always stash + look up under the no-slash form.
  const stripSlash = (p: string) => (p.length > 1 && p.endsWith("/") ? p.slice(0, -1) : p);
  const normalizedPath = stripSlash(path);
  if (PERSISTED_API_PATHS.has(normalizedPath) || PERSISTED_API_PATHS.has(path)) {
    return normalizedPath;
  }
  if (PERSISTED_API_PATHS_WITH_QUERY.has(normalizedPath) || PERSISTED_API_PATHS_WITH_QUERY.has(path)) {
    return `${normalizedPath}${queryStr}`;
  }
  return null;
};

/** Tee a successful Response: parse the body, store under its URL
 *  key in IDB, then return a fresh Response so the caller still
 *  reads the body normally. ``put`` swallows errors — failure to
 *  cache should never break a successful online fetch. */
const persistResponse = async (key: string, response: Response): Promise<void> => {
  try {
    const text = await response.clone().text();
    let body: unknown;
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
    await putRecord(STORE_API_CACHE, { key, body, savedAt: Date.now() });
  } catch (_) {
    /* swallow */
  }
};

/** Write-through tee for the *typed* stores (STORE_TASKS,
 *  STORE_PROJECTS, STORE_ANNOTATIONS, STORE_DRAFTS). When an online
 *  GET succeeds, the payload is auto-persisted so the same URL
 *  works offline later without an explicit "Download for offline"
 *  step. Fills the "annotator opens a task they never explicitly
 *  downloaded, then goes offline" gap flagged in Phase 4.
 *
 *  Fire-and-forget from the caller. Silent on any error — a failed
 *  cache write must never break the online path.
 *
 *  Pattern table sits alongside ``READABLE_PATTERNS`` so add / remove
 *  a URL in one place changes both the read and write side.
 */
type PathPart<T = unknown> = { re: RegExp; put: (m: RegExpMatchArray, body: T) => Promise<void> };
const TYPED_TEE_PATTERNS: PathPart<any>[] = [
  // Single project detail — /api/projects/<id>/
  {
    re: /^\/api\/projects\/(\d+)\/?$/,
    put: async (_m, body) => {
      if (body && typeof body === "object" && body.id != null) {
        await putRecord(STORE_PROJECTS, body);
      }
    },
  },
  // Project list — /api/projects/  (paginated DRF envelope or raw array)
  {
    re: /^\/api\/projects\/?$/,
    put: async (_m, body) => {
      const list = Array.isArray(body) ? body : Array.isArray(body?.results) ? body.results : null;
      if (!list) return;
      await Promise.all(list.filter((p: any) => p && p.id != null).map((p: any) => putRecord(STORE_PROJECTS, p)));
      // Prune: a project the server no longer lists (deleted, or a
      // rolled-back create) must not linger in the mirror as a ghost
      // — the iPad showed a half-created project that existed only
      // locally. Only for the UNFILTERED first-page list of the active
      // org (a paginated/filtered response can't be trusted to be the
      // full set); ids present in the response are the whole truth.
      try {
        const isFullList = !("count" in (body ?? {})) || (body.count ?? 0) <= list.length;
        if (isFullList) {
          const liveIds = new Set(list.map((p: any) => p.id));
          const stored = await getAllRecords<{ id: number; organization?: number | null }>(STORE_PROJECTS);
          const whoami = await getRecord<{ key: string; body: any }>(STORE_API_CACHE, "/api/current-user/whoami");
          const activeOrg = whoami?.body?.active_organization;
          for (const row of stored) {
            if (liveIds.has(row.id)) continue;
            // Only prune rows in the same org (or org-less rows) — other
            // orgs' downloads must survive an org switch.
            if (row.organization != null && activeOrg != null && row.organization !== activeOrg) continue;
            await deleteRecord(STORE_PROJECTS, row.id).catch(() => {});
          }
        }
      } catch (_) {
        /* pruning is best-effort */
      }
    },
  },
  // Single task detail — /api/tasks/<id>/  and  /api/dm/tasks/<id>/
  {
    re: /^\/api\/(?:dm\/)?tasks\/(\d+)\/?$/,
    put: async (_m, body) => {
      if (body && typeof body === "object" && body.id != null) {
        await putRecord(STORE_TASKS, body);
      }
    },
  },
  // Tasks list — /api/tasks/?project=N  and  /api/dm/tasks/?project=N
  // DM shape is ``{tasks, total}``; the cloud's non-DM shape is a
  // raw array; DRF envelope is ``{results, count}``. Handle all three.
  {
    // Both the flat task list AND the per-project list — the latter is
    // what "Download for offline" and the P5 mirror warm-up fetch, so
    // it must land in STORE_TASKS the same way.
    re: /^\/api\/(?:dm\/)?tasks\/?$|^\/api\/projects\/\d+\/tasks\/?$/,
    put: async (_m, body) => {
      const list = Array.isArray(body?.tasks)
        ? body.tasks
        : Array.isArray(body)
          ? body
          : Array.isArray(body?.results)
            ? body.results
            : null;
      if (!list) return;
      await Promise.all(list.filter((t: any) => t && t.id != null).map((t: any) => putRecord(STORE_TASKS, t)));
    },
  },
  // Annotations list for a task
  {
    re: /^\/api\/tasks\/(\d+)\/annotations\/?$/,
    put: async (m, body) => {
      const list = Array.isArray(body) ? body : Array.isArray(body?.results) ? body.results : null;
      if (!list) return;
      const taskId = Number(m[1]);
      await Promise.all(
        list
          .filter((a: any) => a && a.id != null)
          // Stamp ``task`` when the row didn't carry it (some list
          // endpoints omit the FK in favour of context). The
          // ``by_task`` index on STORE_ANNOTATIONS needs it or the
          // offline read fallback comes up empty.
          .map((a: any) => putRecord(STORE_ANNOTATIONS, { task: taskId, ...a })),
      );
    },
  },
  // Drafts list for a task
  {
    re: /^\/api\/tasks\/(\d+)\/drafts\/?$/,
    put: async (m, body) => {
      const list = Array.isArray(body) ? body : Array.isArray(body?.results) ? body.results : null;
      if (!list) return;
      const taskId = Number(m[1]);
      await Promise.all(
        list.filter((d: any) => d && d.id != null).map((d: any) => putRecord(STORE_DRAFTS, { task: taskId, ...d })),
      );
    },
  },
];

const teeToTypedStore = async (url: string, response: Response): Promise<void> => {
  let path = url;
  try {
    if (url.startsWith("http")) path = new URL(url).pathname;
  } catch {
    /* ignore */
  }
  // Fast reject: no pattern will match a URL that doesn't include one
  // of these prefixes. Saves a JSON parse on the ~95% of GETs that
  // aren't cacheable (settings pages, static, misc DM endpoints, …).
  if (!path.startsWith("/api/projects") && !path.startsWith("/api/tasks") && !path.startsWith("/api/dm/tasks")) {
    return;
  }
  let match: { re: RegExp; put: (m: RegExpMatchArray, body: any) => Promise<void>; m: RegExpMatchArray } | null = null;
  for (const p of TYPED_TEE_PATTERNS) {
    const m = p.re.exec(path);
    if (m) {
      match = { ...p, m };
      break;
    }
  }
  if (!match) return;
  try {
    const text = await response.clone().text();
    let body: unknown;
    try {
      body = JSON.parse(text);
    } catch {
      return; /* non-JSON — nothing to cache */
    }
    await match.put(match.m, body);
  } catch (_) {
    /* swallow */
  }
};

/** Read a previously-persisted response from IDB and return it as a
 *  Response object. Returns ``null`` when nothing is cached. */
const replayPersisted = async (key: string): Promise<Response | null> => {
  try {
    const row = await getRecord<{ key: string; body: unknown; savedAt: number }>(STORE_API_CACHE, key);
    if (!row) return null;
    return new Response(JSON.stringify(scrubExpiredSignedUrls(row.body)), {
      status: 200,
      headers: {
        "Content-Type": "application/json",
        "X-Afri-Source": "idb-api-cache",
      },
    });
  } catch (_) {
    return null;
  }
};

/** Signed storage URLs (GCS v4 / S3 presign) expire; a cached whoami
 *  or project payload replayed a day later carries dead ``avatar`` /
 *  ``logo`` links, and the <img> silently 403s → broken avatar. Walk
 *  the cached body and blank any signed URL whose window has passed
 *  so the UI falls back to initials until the live revalidation
 *  refreshes the row. Only ``avatar`` and ``logo`` keys — task media
 *  is handled by the url_content store, not this path. */
const signedUrlExpired = (u: unknown): boolean => {
  if (typeof u !== "string" || !/[?&]X-(Goog|Amz)-(Date|Expires)=/.test(u)) return false;
  try {
    const q = new URL(u).searchParams;
    const date = q.get("X-Goog-Date") || q.get("X-Amz-Date");
    const secs = Number(q.get("X-Goog-Expires") || q.get("X-Amz-Expires"));
    if (!date || !Number.isFinite(secs)) return false;
    const m = date.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/);
    if (!m) return false;
    const signedAt = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]);
    return Date.now() > signedAt + secs * 1000 - 60_000; // 1 min safety
  } catch (_) {
    return false;
  }
};

const scrubExpiredSignedUrls = (body: unknown): unknown => {
  const walk = (v: unknown, depth: number): unknown => {
    if (depth > 4 || v == null || typeof v !== "object") return v;
    if (Array.isArray(v)) return v.map((x) => walk(x, depth + 1));
    const o = v as Record<string, unknown>;
    let out: Record<string, unknown> | null = null;
    for (const k of Object.keys(o)) {
      const val = o[k];
      if ((k === "avatar" || k === "logo" || k === "avatar_url" || k === "logo_url") && signedUrlExpired(val)) {
        out = out ?? { ...o };
        out[k] = null;
      } else if (val && typeof val === "object") {
        const w = walk(val, depth + 1);
        if (w !== val) {
          out = out ?? { ...o };
          out[k] = w;
        }
      }
    }
    return out ?? o;
  };
  return walk(body, 0);
};

/** GET-only patterns that fall back to IndexedDB when the network
 *  fails (or when ``navigator.onLine`` is false). Matches the same
 *  endpoints ``offline-sync.ts``'s ``downloadProjectForOffline``
 *  populates — keeps the read + write contract symmetric.
 *
 *  Each entry returns the JSON body (object or array) — the caller
 *  wraps arrays in ``{results, count}`` so the response shape
 *  matches DRF's pagination contract that LSF / DM expect. */
/** Coerce ``null`` to ``[]`` on every field DM's MST TaskModel
 *  expects to be an array. The cloud often returns ``null`` for
 *  un-touched annotators / reviewers / updated_by / etc.; the
 *  cached IDB snapshot preserved that, but MST's typecheck rejects
 *  ``Value is not an array`` and crashes ``ModelType.create`` /
 *  ``applySnapshot``. Apply on every task we hand back from offline
 *  storage. */
const TASK_ARRAY_FIELDS = [
  "annotators",
  "reviewers",
  "updated_by",
  "comment_authors",
  "annotations",
  "predictions",
  "drafts",
];
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const sanitizeTask = (task: any): any => {
  if (!task || typeof task !== "object") return task;
  const out: Record<string, unknown> = { ...task };
  for (const k of TASK_ARRAY_FIELDS) {
    const v = out[k];
    if (v == null) {
      out[k] = [];
    } else if (!Array.isArray(v)) {
      // Wrap primitives + single-object values: cloud sometimes
      // returns ``updated_by: 11`` (user id) instead of
      // ``updated_by: [11]``. DM's Assignee preprocessor accepts a
      // raw number and inflates to ``{id, user, …}``, but the
      // outer field type is ``array(Assignee)`` so the wrapper has
      // to be an array. Box once.
      out[k] = [v];
    }
  }
  return out;
};

/** Read-side fallback patterns. Each entry's ``fetch`` receives the
 *  regex match AND a URLSearchParams view of the original URL's
 *  query string — so DM endpoints that key off ``?project=N`` can
 *  read it without re-parsing. Returning ``null`` / ``undefined``
 *  lets the caller fall through to the next strategy. */
const READABLE_PATTERNS: Array<{
  re: RegExp;
  fetch: (m: RegExpMatchArray, q: URLSearchParams) => Promise<unknown>;
}> = [
  {
    // Projects LIST. Returns every cached project that belongs to
    // the user's currently-active organization. Without the org
    // filter, switching orgs offline would surface every project
    // across orgs in one combined list — leaks identity boundaries
    // and confuses annotators who'd expect an empty list when in
    // an org they haven't downloaded for.
    //
    // Active org comes from the cached ``whoami`` response in
    // STORE_API_CACHE. When that's unavailable (first session,
    // never online) we fall back to returning everything cached —
    // safer than returning empty since the user may be checking
    // their downloaded set before any org switching has happened.
    re: /^\/api\/projects\/?$/,
    fetch: async () => {
      const all = await getAllRecords<{ organization?: number | null }>(STORE_PROJECTS);
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const whoami = await getRecord<{ key: string; body: any }>(STORE_API_CACHE, "/api/current-user/whoami");
        const activeOrg = whoami?.body?.active_organization;
        if (activeOrg != null) {
          // Only filter rows that CARRY an organization. The list
          // endpoint is org-scoped server-side and its ``include=``
          // projections often omit ``organization`` — those rows were
          // stored from the active org's own list, so an
          // ``undefined === activeOrg`` check dropped every one of
          // them and mirror-first answered "no projects" while the
          // mirror was full.
          return all.filter((p) => p?.organization == null || p.organization === activeOrg);
        }
      } catch (_) {
        /* fall through with full set */
      }
      return all;
    },
  },
  {
    re: /^\/api\/projects\/(\d+)\/?$/,
    fetch: async (m) => getRecord(STORE_PROJECTS, Number(m[1])),
  },
  {
    // Listen (crowd validation) next-clip — served from the clip pack
    // that "Download for offline" cached, skipping clips whose vote is
    // already queued in the outbox. When the pack runs dry the page
    // gets the normal "all done" payload, never an error. Votes POST
    // through the outbox; the server's 409-on-duplicate makes replays
    // safe (see ClipVote unique constraint).
    re: /^\/api\/projects\/(\d+)\/validate\/next\/?$/,
    fetch: async (m) => {
      const projectId = Number(m[1]);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const rec = await getRecord<{ key: string; body: any }>(STORE_API_CACHE, `validate_batch:${projectId}`);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const body: any = rec?.body ?? {};
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const clips: any[] = Array.isArray(body?.clips) ? body.clips : [];
      const votedIds = new Set<number>();
      try {
        for (const e of await list()) {
          const mm = /\/api\/annotations\/(\d+)\/validate\/?$/.exec(e.url || "");
          if (mm) votedIds.add(Number(mm[1]));
        }
      } catch (_) {
        /* outbox unreadable — serve the pack unfiltered */
      }
      const pending = clips.filter((c) => c?.annotation_id && !votedIds.has(Number(c.annotation_id)));
      if (pending.length) {
        return { __noWrap: true, ...pending[0], remaining_estimate: pending.length };
      }
      return {
        __noWrap: true,
        annotation_id: null,
        remaining_estimate: 0,
        criteria: body?.criteria ?? [],
        profile: body?.profile ?? "custom",
        prompts_enabled: !!body?.prompts_enabled,
        prompts_max_words: body?.prompts_max_words ?? 20,
      };
    },
  },
  {
    // Discover feed + "my contributions" — replay the last-seen
    // response for this exact query; fall back to the most recent
    // cached page of the same endpoint (a search the user never ran
    // online replays the default feed rather than erroring). Join
    // stays online-only: the POST is not queueable and the UI
    // disables it offline.
    re: /^\/api\/discover(\/mine)?\/?$/,
    fetch: async (m, q) => {
      const path = m[1] ? "/api/discover/mine" : "/api/discover";
      const qs = q.toString();
      try {
        if (qs) {
          const exact = await getRecord<{ key: string; body: unknown }>(STORE_API_CACHE, `${path}?${qs}`);
          if (exact?.body) return { __noWrap: true, ...(exact.body as Record<string, unknown>) };
        }
        const bare = await getRecord<{ key: string; body: unknown }>(STORE_API_CACHE, path);
        if (bare?.body) return { __noWrap: true, ...(bare.body as Record<string, unknown>) };
        const all = await getAllRecords<{ key: string; body: unknown; savedAt?: number }>(STORE_API_CACHE);
        const candidates = all
          .filter((r) => typeof r?.key === "string" && (r.key === path || r.key.startsWith(`${path}?`)))
          .sort((a, b) => (b.savedAt ?? 0) - (a.savedAt ?? 0));
        if (candidates[0]?.body) return { __noWrap: true, ...(candidates[0].body as Record<string, unknown>) };
      } catch (_) {
        /* fall through */
      }
      // Cold offline boot: an empty-but-valid feed beats an error page.
      return { __noWrap: true, results: [], count: 0 };
    },
  },
  {
    re: /^\/api\/projects\/(\d+)\/tasks\/?$/,
    fetch: async (m) => getAllByIndex(STORE_TASKS, "by_project", Number(m[1])),
  },
  {
    re: /^\/api\/tasks\/(\d+)\/?$/,
    fetch: async (m) => sanitizeTask(await getRecord(STORE_TASKS, Number(m[1]))),
  },
  {
    // DM tasks LIST (``/api/tasks?project=N&page=...&view=...``).
    // Built from gateway ``/api/dm`` + endpoint ``/../tasks`` →
    // ``/api/tasks``. DM destructures the response as
    // ``{ total, [apiMethod]: list } = data`` (see
    // ``mixins/DataStore/DataStore.js`` line 281), so the response
    // shape MUST be ``{tasks: [...], total: N}`` — not the generic
    // ``{results, count}`` DRF pagination envelope. Returning the
    // wrong shape leaves the grid empty even when IDB has rows.
    // ``__noWrap`` opts out of tryReadFromIdb's auto-wrap so this
    // shape ships through unchanged.
    re: /^\/api\/tasks\/?$/,
    fetch: async (_m, q) => {
      const projectId = Number(q.get("project") || 0);
      if (!projectId) return null;
      const tasks = await getAllByIndex<unknown>(STORE_TASKS, "by_project", projectId);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const sanitized = (tasks as any[]).map(sanitizeTask);
      return {
        __noWrap: true,
        tasks: sanitized,
        total: sanitized.length,
      };
    },
  },
  {
    re: /^\/api\/tasks\/(\d+)\/annotations\/?$/,
    fetch: async (m) => getAllByIndex(STORE_ANNOTATIONS, "by_task", Number(m[1])),
  },
  {
    re: /^\/api\/tasks\/(\d+)\/drafts\/?$/,
    fetch: async (m) => getAllByIndex(STORE_DRAFTS, "by_task", Number(m[1])),
  },
  // Data Manager paths proxy to /api/tasks/* — same IDB rows.
  {
    re: /^\/api\/dm\/tasks\/(\d+)\/?$/,
    fetch: async (m) => getRecord(STORE_TASKS, Number(m[1])),
  },
  {
    // DM label stream — "give me the next task to label". Without a
    // server, "next" = first task we have cached for this project
    // that doesn't already have an annotation in IDB. Forgiving:
    // when the project query param is missing, fall through (caller
    // gets null and can render an empty state).
    re: /^\/api\/dm\/tasks\/next\/?$/,
    fetch: async (_m, q) => {
      const projectId = Number(q.get("project") || 0);
      if (!projectId) return null;
      const tasks = await getAllByIndex<{ id: number }>(STORE_TASKS, "by_project", projectId);
      // Sort by id so the user gets a deterministic order across
      // sessions; cheap proxy for the cloud's default ordering.
      tasks.sort((a, b) => a.id - b.id);
      for (const t of tasks) {
        const anns = await getAllByIndex<unknown>(STORE_ANNOTATIONS, "by_task", t.id);
        if (!anns || anns.length === 0) return t;
      }
      // Nothing un-annotated — return the first task so the LSF
      // shell still has a frame to render. Cloud returns 404 in
      // this case; we return the task itself instead.
      return tasks[0] ?? null;
    },
  },
  {
    // DM tasks list — paginated by the server normally; we ignore
    // pagination + filters here and just return everything cached
    // for the project. Same shape as the non-DM ``/api/tasks``
    // handler above: ``{tasks, total}`` (with ``__noWrap`` so the
    // auto-wrap doesn't repackage as ``{results, count}`` and leave
    // DM's grid empty). Without this, DM's ``DataStore`` does
    // ``{ total, [apiMethod]: list } = data`` against a ``{results,
    // count}`` envelope and the destructured ``tasks`` is undefined
    // → grid renders zero rows even when STORE_TASKS has hundreds.
    re: /^\/api\/dm\/tasks\/?$/,
    fetch: async (_m, q) => {
      const projectId = Number(q.get("project") || 0);
      if (!projectId) return null;
      const tasks = await getAllByIndex<unknown>(STORE_TASKS, "by_project", projectId);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const sanitized = (tasks as any[]).map(sanitizeTask);
      return {
        __noWrap: true,
        tasks: sanitized,
        total: sanitized.length,
      };
    },
  },
  {
    // DM project summary — same payload as /api/projects/<id> but
    // routed via the DM gateway. Resolve from STORE_PROJECTS.
    re: /^\/api\/dm\/project\/?$/,
    fetch: async (_m, q) => {
      const projectId = Number(q.get("project") || 0);
      if (!projectId) return null;
      return getRecord(STORE_PROJECTS, projectId);
    },
  },
  {
    // DM columns — column definitions for the task table. Critical
    // because the columns drive ``DynamicModel`` which builds the
    // MST TaskModel: any field NOT declared here gets dropped from
    // ``task`` after MST processes the snapshot. Empty columns
    // would mean ``task.data`` is silently lost → LSF receives
    // ``{data: undefined}`` → RichText.dataObj is null → text
    // renders blank. Strategy:
    //
    //   1. Prefer a previously-cached real response (persisted via
    //      the ``PERSISTED_API_PATHS_WITH_QUERY`` write-tee on a
    //      successful online fetch).
    //   2. Fall back to a hand-rolled minimum that at least
    //      declares ``data`` + the per-task counters DM grid uses,
    //      so labelling still works even on a cold offline boot.
    re: /^\/api\/dm\/columns\/?$/,
    fetch: async (_m, q) => {
      const projectId = q.get("project") || "";
      const cacheKey = `/api/dm/columns?project=${projectId}`;
      try {
        const row = await getRecord<{ key: string; body: unknown }>(STORE_API_CACHE, cacheKey);
        if (row?.body) return row.body;
      } catch (_) {
        /* fall through */
      }
      // Minimum viable column set. Only what TaskModel needs to
      // round-trip task data without dropping fields. ``data`` is
      // the load-bearing one — without it, every text/audio/csv
      // task renders empty offline.
      return {
        columns: [
          { id: "data", type: "List", title: "Data", target: "tasks", parent: null, children: null },
          { id: "completed_at", type: "Datetime", title: "Completed", target: "tasks", parent: null, children: null },
          { id: "created_at", type: "Datetime", title: "Created", target: "tasks", parent: null, children: null },
          { id: "updated_at", type: "Datetime", title: "Updated", target: "tasks", parent: null, children: null },
          {
            id: "total_annotations",
            type: "Number",
            title: "Annotations",
            target: "tasks",
            parent: null,
            children: null,
          },
          {
            id: "cancelled_annotations",
            type: "Number",
            title: "Cancelled",
            target: "tasks",
            parent: null,
            children: null,
          },
          {
            id: "total_predictions",
            type: "Number",
            title: "Predictions",
            target: "tasks",
            parent: null,
            children: null,
          },
          { id: "annotators", type: "List", title: "Annotators", target: "tasks", parent: null, children: null },
          { id: "reviewers", type: "List", title: "Reviewers", target: "tasks", parent: null, children: null },
          { id: "is_labeled", type: "Boolean", title: "Labeled", target: "tasks", parent: null, children: null },
          { id: "file_upload", type: "String", title: "Source", target: "tasks", parent: null, children: null },
          { id: "inner_id", type: "Number", title: "ID", target: "tasks", parent: null, children: null },
        ],
      };
    },
  },
  {
    // DM views (tabs) — return one synthetic default view so the
    // DM has somewhere to render tasks. The DM expects EITHER an
    // array OR an object with a ``tabs`` field; the array form
    // gets wrapped by ``tryReadFromIdb`` into ``{results, count}``,
    // which DM mis-reads as a single tab (object) and crashes with
    // ``tabs.map is not a function``. Return the {tabs: [...]} form
    // explicitly so DM's own ``response.tabs ?? response`` chain
    // resolves to the array directly.
    re: /^\/api\/dm\/views\/?$/,
    fetch: async (_m, q) => {
      const projectId = Number(q.get("project") || 0);
      return {
        __noWrap: true,
        tabs: [
          {
            id: 1,
            project: projectId || null,
            title: "All",
            type: "list",
            data: { filters: { conjunction: "and", items: [] }, ordering: [] },
          },
        ],
      };
    },
  },
];

// Diagnostic flag — set to true to console.log every IDB read +
// shape so we can spot when the cached task is missing fields.
// Cheap to leave on while we're stabilising offline; flip off once
// the time-series / align flows are confirmed.
// Verbose per-hit logging of IDB reads. Ship OFF — it dumps a line
// per offline read (incl. truncated label_configs) into the WebView
// console. Flip locally when debugging the offline read path.
const DEBUG_IDB_READS = false;

const tryReadFromIdb = async (url: string): Promise<Response | null> => {
  let path = url;
  let queryStr = "";
  try {
    if (url.startsWith("http")) {
      const u = new URL(url);
      path = u.pathname;
      queryStr = u.search;
    } else {
      const q = url.indexOf("?");
      if (q !== -1) {
        path = url.slice(0, q);
        queryStr = url.slice(q);
      }
    }
  } catch {
    /* keep raw */
  }
  const params = new URLSearchParams(queryStr.startsWith("?") ? queryStr.slice(1) : queryStr);
  for (const { re, fetch: lookup } of READABLE_PATTERNS) {
    const m = path.match(re);
    if (!m) continue;
    try {
      const data = await lookup(m, params);
      if (DEBUG_IDB_READS) {
        try {
          // eslint-disable-next-line no-console
          const summary =
            data == null
              ? "null"
              : Array.isArray(data)
                ? `array(${data.length})`
                : typeof data === "object"
                  ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    `keys=${Object.keys(data as any).join(",")}`
                  : typeof data;
          // eslint-disable-next-line no-console
          console.info(
            "[offline-fetch] idb-hit",
            path,
            summary,
            // For task-shaped responses surface whether data.<...> exists
            // so we can see if the cached row is missing its stimulus.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (data as any)?.data ? `data-keys=${Object.keys((data as any).data).join(",")}` : "no-data",
          );
          // For project payloads, dump the label_config (truncated)
          // so we can see exactly which tag attrs are set — the
          // difference between ``<Text value="$text"/>`` (renders
          // inline) vs ``<Text value="$text" valueType="url"/>``
          // (tries to fetch) is the most common reason a tag mounts
          // but renders empty.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const label = (data as any)?.label_config;
          if (typeof label === "string" && label.length > 0) {
            // eslint-disable-next-line no-console
            console.info("[offline-fetch] project label_config:", label.slice(0, 600));
          }
        } catch (_) {
          /* swallow */
        }
      }
      if (data == null) return null;
      // Auto-wrap arrays into the DRF pagination shape (``{results,
      // count}``) UNLESS the handler explicitly opted out via the
      // ``__noWrap`` sentinel — some endpoints (DM views, etc.)
      // expect a different shape and must control their own response.
      let body: unknown;
      if (Array.isArray(data)) {
        body = { results: data, count: data.length };
      } else if (data && typeof data === "object" && (data as { __noWrap?: boolean }).__noWrap) {
        const { __noWrap, ...rest } = data as Record<string, unknown> & { __noWrap?: boolean };
        void __noWrap;
        body = rest;
      } else {
        body = data;
      }
      return new Response(JSON.stringify(body), {
        status: 200,
        headers: { "Content-Type": "application/json", "X-Afri-Source": "idb" },
      });
    } catch {
      return null;
    }
  }
  return null;
};

/** Exact write surfaces that queue offline: annotation and draft
 *  mutations, nothing else. Everything outside this list — project
 *  settings, prompts, profile edits — falls through to the regular
 *  fetch and errors normally when offline, because queueing it would
 *  return a fake 201 the caller reads as a saved settings change.
 *  (The pre-P1 prefix list swept in ALL of ``/api/projects/`` and
 *  ``/api/tasks/`` writes, which did exactly that.)
 *
 *  The task-scoped create pattern matters most: an offline Submit
 *  POST to ``/api/tasks/4/annotations`` must queue — the synthetic
 *  response's ``id`` is what lets LSF track the new annotation and
 *  the next-task loop advance instead of re-serving the same task. */
const QUEUEABLE_RES = [
  // Annotation create (LSF Submit) + draft create/autosave under a task.
  /^\/api\/tasks\/\d+\/(annotations|drafts)(\/|$)/,
  // Annotation update / delete / Listen validate vote. ``-\d`` covers
  // placeholder ids from offline creates (remapped before replay).
  /^\/api\/annotations\/-?\d+(\/|$)/,
  // Draft update / delete / promote.
  /^\/api\/drafts\/-?\d+(\/|$)/,
];

const isWriteMethod = (m: string): boolean => ["POST", "PUT", "PATCH", "DELETE"].includes((m || "GET").toUpperCase());

const isQueueableUrl = (url: string): boolean => {
  let path = url;
  try {
    // Absolute URLs come from the API config; pull out pathname.
    if (url.startsWith("http")) path = new URL(url).pathname;
  } catch {
    // ignore — keep the raw URL as-is
  }
  const q = path.indexOf("?");
  if (q !== -1) path = path.slice(0, q);
  return QUEUEABLE_RES.some((re) => re.test(path));
};

/** Capture the original fetch BEFORE the wrapper installs so the
 *  sync runner can replay outbox entries against the network
 *  without recursing through the wrapper. */
const originalFetch: typeof fetch = typeof window !== "undefined" ? window.fetch.bind(window) : fetch;

/** Network-only fetch for callers that must NEVER be answered from
 *  the offline IDB mirror — above all ``downloadProjectForOffline``:
 *  its whole job is to fill that mirror, so a wrapped read served
 *  from an empty mirror makes the download "succeed" with 0 rows
 *  (observed on-device when the connectivity probe was mid-flap).
 *  Still goes through the service worker, so media prewarm keeps
 *  populating the SW cache. */
export const networkFetch: typeof fetch = originalFetch;

const headersToObject = (h: HeadersInit | undefined): Record<string, string> => {
  if (!h) return {};
  if (h instanceof Headers) {
    const out: Record<string, string> = {};
    h.forEach((v, k) => {
      out[k] = v;
    });
    return out;
  }
  if (Array.isArray(h)) return Object.fromEntries(h);
  return { ...(h as Record<string, string>) };
};

const stringifyBody = async (body: BodyInit | null | undefined): Promise<string | null> => {
  if (body == null) return null;
  if (typeof body === "string") return body;
  if (body instanceof Blob) return await body.text();
  if (body instanceof URLSearchParams) return body.toString();
  if (body instanceof FormData) {
    // Serialise as JSON for replay. Files inside FormData are
    // currently dropped — annotation submissions are JSON in
    // practice; if a future flow needs file uploads offline
    // (audio recordings) we'll add a Blob store + replay path.
    const obj: Record<string, unknown> = {};
    body.forEach((v, k) => {
      obj[k] = v instanceof Blob ? "[blob omitted]" : v;
    });
    return JSON.stringify(obj);
  }
  // ArrayBuffer / TypedArray etc. — not used by the React annotation
  // path today. Store as a placeholder so the entry is still
  // visible; replay will likely 400 if reached.
  return JSON.stringify({ __unsupported_body: true });
};

const synthetic202 = (queuedId: number, extra?: Record<string, unknown>): Response => {
  // Optimistic response so the calling code doesn't surface an
  // error. Body mirrors what a real 201/202 from the cloud might
  // emit — ``id`` is what LSF reads to track the new annotation,
  // synthesised from the outbox row id (negative-prefixed to make
  // it obvious in DB inspectors that it's a placeholder, and to
  // never collide with real cloud annotation ids which are positive
  // integers). ``X-Afri-Queued`` lets callers detect that the body
  // came from the outbox path.
  const body = { queued: true, queued_id: queuedId, id: -queuedId, ...(extra ?? {}) };
  return new Response(JSON.stringify(body), {
    status: 201,
    statusText: "Queued offline",
    headers: { "Content-Type": "application/json", "X-Afri-Queued": String(queuedId) },
  });
};

/** Mirror an offline annotation/draft write into the read store so
 *  the next-task heuristic + the DM grid see the task as already
 *  annotated. The outbox replay will eventually replace this with
 *  the cloud-issued row, but until then the offline UI stays
 *  internally consistent: Submit on task N → next_task returns
 *  task N+1, not N again.
 *
 *  Three IDB stores are updated in tandem:
 *    1. STORE_ANNOTATIONS (or STORE_DRAFTS) — the placeholder row
 *       used by ``/api/tasks/:id/annotations`` IDB reads + the
 *       next_task heuristic.
 *    2. STORE_TASKS — appends the new annotation to the task's
 *       inline ``annotations`` array, bumps ``total_annotations``,
 *       and flips ``is_labeled`` to true. Without this, reopening
 *       the task locally shows zero annotations because LSF reads
 *       them from the bundled task payload, not the per-task
 *       endpoint.
 *    3. STORE_PROJECTS — bumps ``num_tasks_with_annotations`` and
 *       ``useful_annotation_number`` so the projects list "X/Y
 *       annotated" counter reflects local submits. Only bumps when
 *       this task was previously unannotated to keep the counter
 *       consistent across re-submits.
 *
 *  Best-effort; failure to write the mirror should never break the
 *  outbox enqueue (the user's annotation is still safe). */
const mirrorAnnotationWrite = async (
  url: string,
  method: string,
  bodyText: string | null,
  queuedId: number,
): Promise<void> => {
  try {
    if (method !== "POST") return; // we only mirror creates; updates touch existing rows
    let path = url;
    if (url.startsWith("http")) path = new URL(url).pathname;
    const q = path.indexOf("?");
    if (q !== -1) path = path.slice(0, q);
    // Annotation create: POST /api/tasks/:taskID/annotations[/]
    const annM = path.match(/^\/api\/tasks\/(\d+)\/annotations\/?$/);
    // Draft create (label-stream first save): POST /api/tasks/:id/drafts/
    // and POST /api/tasks/:id/annotations/:annId/drafts/
    const draftM =
      path.match(/^\/api\/tasks\/(\d+)\/drafts\/?$/) ??
      path.match(/^\/api\/tasks\/(\d+)\/annotations\/\d+\/drafts\/?$/);
    if (!annM && !draftM) return;
    const taskId = Number((annM ?? draftM)![1]);
    if (!taskId) return;
    let parsedBody: Record<string, unknown> = {};
    if (bodyText) {
      try {
        parsedBody = JSON.parse(bodyText);
      } catch {
        /* keep empty */
      }
    }
    const placeholderId = -queuedId; // matches the synthetic 202 body
    const now = new Date().toISOString();
    const row = {
      ...parsedBody,
      id: placeholderId,
      task: taskId,
      _afri_offline_pending: true,
      created_at: now,
      updated_at: now,
    };
    await putRecord(annM ? STORE_ANNOTATIONS : STORE_DRAFTS, row);

    // Patch the cached task — DM grid + LSF re-open both read these
    // fields. Only do this for annotations (not drafts) since drafts
    // shouldn't mark a task ``is_labeled``.
    if (annM) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const cachedTask = await getRecord<any>(STORE_TASKS, taskId);
        if (cachedTask) {
          const existingAnns = Array.isArray(cachedTask.annotations) ? cachedTask.annotations : [];
          const wasUnannotated = existingAnns.length === 0 && (cachedTask.total_annotations ?? 0) === 0;
          const updatedTask = {
            ...cachedTask,
            annotations: [...existingAnns, row],
            total_annotations: (cachedTask.total_annotations ?? 0) + 1,
            // ``cancelled_annotations`` only bumps when the user
            // skipped — Skip routes through the same endpoint with
            // ``was_cancelled: true`` in the body.
            cancelled_annotations:
              parsedBody?.was_cancelled === true
                ? (cachedTask.cancelled_annotations ?? 0) + 1
                : (cachedTask.cancelled_annotations ?? 0),
            is_labeled: true,
            updated_at: now,
            // ``completed_at`` is what DM's grid renders in the
            // "Completed" column. Without it, the row stays blank
            // even though ``is_labeled`` is true, because the cell
            // formatter pulls from this field. Cloud sets it when
            // the annotation is created; replicate locally so the
            // grid reflects offline submissions.
            completed_at: cachedTask.completed_at ?? now,
          };
          await putRecord(STORE_TASKS, updatedTask);

          // Bump project-level counters so the projects list
          // "X annotated tasks of Y" reflects the local submit.
          // Only when this task was previously unannotated; a
          // re-submit on an already-annotated task doesn't grow
          // the per-task count above 1.
          if (wasUnannotated) {
            const projectId = Number(cachedTask.project);
            if (projectId) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const cachedProject = await getRecord<any>(STORE_PROJECTS, projectId);
              if (cachedProject) {
                await putRecord(STORE_PROJECTS, {
                  ...cachedProject,
                  num_tasks_with_annotations: (cachedProject.num_tasks_with_annotations ?? 0) + 1,
                  useful_annotation_number: (cachedProject.useful_annotation_number ?? 0) + 1,
                  total_annotations_number: (cachedProject.total_annotations_number ?? 0) + 1,
                  finished_task_number: (cachedProject.finished_task_number ?? 0) + 1,
                });
              }
            }
          }
        }
      } catch (_) {
        /* swallow — annotation mirror still wrote */
      }
    }
    // eslint-disable-next-line no-console
    console.info(
      "[offline-fetch] mirrored offline write",
      annM ? "annotation" : "draft",
      "task=",
      taskId,
      "placeholder-id=",
      placeholderId,
    );
  } catch (_) {
    /* swallow — outbox is the source of truth */
  }
};

/** Native network state from ``@capacitor/network``, when the plugin
 *  is present in the shell. ``null`` = unknown (plugin missing /
 *  listener not yet fired) — treat as online and let the fetch
 *  timeout decide, the pre-P1 behaviour. This flag is what removes
 *  the 6-second stall on every offline write: ``navigator.onLine``
 *  is permanently ``true`` inside the Capacitor WebView, so without
 *  the plugin the queueing path is only ever reached via timeout. */
let nativeConnected: boolean | null = null;

/* ── Offline-first reads (mirror-first + background revalidate) ──── */

/** Master switch. Off = the pre-P5 behaviour (network first, mirror
 *  only on failure). Kept as a flag so a bad interaction can be turned
 *  off from the console (``localStorage.afri:mirror-first = "0"``)
 *  without a rebuild. */
const MIRROR_FIRST: boolean = (() => {
  try {
    return window.localStorage?.getItem("afri:mirror-first") !== "0";
  } catch (_) {
    return true;
  }
})();

/** Reads that must stay live-only even when the mirror has them:
 *  identity/auth/reachability. Everything else in READABLE_PATTERNS
 *  (projects, tasks, annotations, drafts, DM views/columns, discover)
 *  is mirror-eligible. */
const MIRROR_FIRST_EXCLUDE = [
  /^\/api\/current-user\/whoami/,
  /^\/api\/organizations\/?$/,
  /^\/api\/version/,
  /^\/api\/afri\/auth\//,
  /^\/api\/token\//,
  /^\/api\/feature-flags/,
];

const urlPath = (url: string): string => {
  let path = url;
  try {
    if (url.startsWith("http")) path = new URL(url).pathname;
  } catch (_) {
    /* raw */
  }
  const q = path.indexOf("?");
  return q === -1 ? path : path.slice(0, q);
};

/** True for bodies that carry no rows: ``[]``, ``{results:[]}``,
 *  ``{results:[],count:0}``, ``{}``, ``{tasks:[]}``. */
const isEmptyPayload = (text: string): boolean => {
  const t = text.trim();
  if (t === "[]" || t === "{}" || t === "null" || t === "") return true;
  try {
    const v = JSON.parse(t);
    if (Array.isArray(v)) return v.length === 0;
    if (v && typeof v === "object") {
      for (const k of ["results", "tasks", "items", "data"]) {
        if (Array.isArray(v[k])) return v[k].length === 0;
      }
      return Object.keys(v).length === 0;
    }
  } catch (_) {
    /* not JSON — treat as content */
  }
  return false;
};

const isMirrorFirstUrl = (url: string): boolean => {
  const path = urlPath(url);
  if (!path.startsWith("/api/")) return false;
  return !MIRROR_FIRST_EXCLUDE.some((re) => re.test(path));
};

/** In-flight revalidations, keyed by URL, so a page that requests the
 *  same resource from three components triggers ONE network call. */
const revalidating = new Set<string>();

/** Fetch the live resource, refresh the mirror via the same tee the
 *  online path uses, and tell the UI something changed. Never throws;
 *  a failed revalidate simply leaves the mirror as it was. */
/** Per-URL cooldown: once revalidated, don't re-hit the network for the
 *  same URL for a while even if the page asks again (react-query
 *  refetch storms, StrictMode double-mounts). */
const lastRevalidated = new Map<string, number>();
const REVALIDATE_COOLDOWN_MS = 30_000;

/** Change detection: fingerprint of what the mirror served, per URL,
 *  so ``afri:mirror-updated`` only fires when the cloud's answer is
 *  actually DIFFERENT. Firing on every revalidate produced a loop —
 *  read → revalidate → "updated" → invalidate → read → … */
const servedFingerprint = new Map<string, string>();
const fingerprint = (text: string): string => `${text.length}:${text.slice(0, 64)}:${text.slice(-64)}`;

const revalidateInBackground = (
  url: string,
  headers: HeadersInit | undefined,
  init: RequestInit | undefined,
  servedBody?: string,
): void => {
  if (revalidating.has(url)) return;
  const last = lastRevalidated.get(url) ?? 0;
  if (Date.now() - last < REVALIDATE_COOLDOWN_MS) return;
  revalidating.add(url);
  if (servedBody != null) servedFingerprint.set(url, fingerprint(servedBody));
  const { signal: timeoutSignal, cancel: cancelTimeout } = withTimeout(url, null);
  originalFetch(url, {
    ...(init ?? {}),
    ...(headers !== undefined ? { headers } : {}),
    signal: timeoutSignal,
    cache: "no-store",
  })
    .then(async (res) => {
      cancelTimeout();
      lastRevalidated.set(url, Date.now());
      if (!res.ok) return;
      const ct = res.headers.get("content-type") || "";
      if (ct.includes("text/html")) return;
      const text = await res.clone().text();
      const key = cacheKeyForUrl(url);
      const jobs: Promise<unknown>[] = [teeToTypedStore(url, res.clone())];
      if (key) jobs.push(persistResponse(key, res.clone()));
      await Promise.all(jobs).catch(() => {});
      const before = servedFingerprint.get(url);
      const after = fingerprint(text);
      servedFingerprint.set(url, after);
      if (before !== undefined && before === after) return; // nothing changed — stay quiet
      try {
        window.dispatchEvent(new CustomEvent("afri:mirror-updated", { detail: { url } }));
      } catch (_) {
        /* older webviews */
      }
    })
    .catch(() => {
      cancelTimeout();
    })
    .finally(() => {
      revalidating.delete(url);
    });
};

const isOffline = (): boolean =>
  nativeConnected === false || (typeof navigator !== "undefined" && navigator.onLine === false);

/** Hard timeout for every wrapped fetch. Without this, a request to
 *  a cloud whose TCP socket is half-open (cloud crashed but the
 *  WebView's connection hasn't received a FIN yet) sits forever and
 *  the labeling page hangs on a spinner. The timeout aborts the
 *  fetch, throws, and the catch branch routes the call through the
 *  offline IDB fallback — exactly the behaviour we'd see if the
 *  network was visibly down.
 *
 *  6s strikes a balance: long enough to forgive a momentarily slow
 *  cellular handshake, short enough that "tap Label → see cached
 *  task" feels snappy when the cloud has just been killed.
 *
 *  Long uploads / reads (large media, etc.) get a more generous
 *  budget below — keyed off URL pattern. */
const DEFAULT_TIMEOUT_MS = 6000;
const SLOW_PATH_TIMEOUT_MS = 30000;
/** URL substrings whose requests get the longer timeout. Media
 *  uploads, exports, and bulk imports are the slow paths today. */
const SLOW_PATH_PATTERNS = ["/data/upload/", "/data/local-files/", "/data/export/", "/api/import/", "/api/dm/actions"];

/** Writes (POST/PUT/PATCH/DELETE) always get the LONG budget. A project
 *  delete cascades tasks + annotations server-side and can run well
 *  past 6s; aborting the client at 6s reported "offline" while the
 *  server kept deleting — the user then saw a half-deleted project.
 *  Aborting a write client-side never cancels it server-side, so a
 *  short timeout on writes is pure downside. */
const timeoutForUrl = (url: string, method = "GET"): number =>
  (method !== "GET" && method !== "HEAD") || SLOW_PATH_PATTERNS.some((p) => url.includes(p))
    ? SLOW_PATH_TIMEOUT_MS
    : DEFAULT_TIMEOUT_MS;

/** Merge a timeout AbortSignal with any caller-provided signal so
 *  either source can cancel. Returns ``{signal, cancel}`` — call
 *  ``cancel()`` after the fetch resolves to clear the pending
 *  ``setTimeout`` so we don't leak handles. */
const withTimeout = (
  url: string,
  initSignal: AbortSignal | null | undefined,
  method = "GET",
): { signal: AbortSignal; cancel: () => void } => {
  const ctrl = new AbortController();
  const ms = timeoutForUrl(url, method);
  const timer = setTimeout(() => {
    try {
      ctrl.abort(new DOMException(`offline-fetch timeout after ${ms}ms`, "AbortError"));
    } catch (_) {
      /* swallow */
    }
  }, ms);
  if (initSignal) {
    if (initSignal.aborted) ctrl.abort(initSignal.reason);
    else initSignal.addEventListener("abort", () => ctrl.abort(initSignal.reason), { once: true });
  }
  return {
    signal: ctrl.signal,
    cancel: () => clearTimeout(timer),
  };
};

/** An API endpoint answering with an HTML page is never what the
 *  caller wanted — it's a Django error page (500), a proxy error, or
 *  a session-expired redirect served as 200. Passing it through makes
 *  every caller die in ``JSON.parse`` with the cryptic
 *  "Unrecognized token '<'". Convert to a small JSON error envelope
 *  instead so the UI can show a real message. */
const htmlToJsonEnvelope = (response: Response): Response => {
  const status = response.status >= 400 ? response.status : 502;
  const body = {
    error:
      response.status >= 500
        ? "The server hit an error handling this request. Try again in a moment."
        : response.status >= 400
          ? `Request failed (${response.status}).`
          : "The server answered with a web page instead of data — your session may have expired. Sign in again if this keeps happening.",
    code: response.status >= 400 ? "server_error" : "html_response",
    original_status: response.status,
  };
  return new Response(JSON.stringify(body), {
    status,
    headers: { "Content-Type": "application/json", "X-Afri-Envelope": "html" },
  });
};

const wrappedFetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
  // Normalise to (url, init) form. The Request object form
  // ``new Request(url, init)`` is rare in this codebase (APIProxy
  // passes string + init), but we fall back gracefully.
  let url: string;
  let method = "GET";
  let body: BodyInit | null = null;
  let headers: HeadersInit | undefined;

  if (typeof input === "string") {
    url = input;
  } else if (input instanceof URL) {
    url = input.toString();
  } else {
    url = input.url;
    method = input.method;
    headers = input.headers;
    // Reading the Request body up-front consumes the stream;
    // clone to leave the original intact for the network call.
    body = (await input.clone().blob()) as unknown as BodyInit;
  }

  if (init) {
    method = init.method ?? method;
    headers = init.headers ?? headers;
    body = init.body ?? body;
  }
  method = method.toUpperCase();

  // Canonicalize RELATIVE API urls to the cloud host. The WebView's
  // origin is the local Capacitor server, which answers any unknown
  // path with the SPA's index.html — a component's plain
  // ``fetch("/api/…")`` never reached the cloud at all: it got HTML
  // back, the guard wrapped it as a 502, and the card silently hid
  // (the empty "My work" tab). Absolute URLs pass through untouched.
  if (url.startsWith("/api/")) {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const cloud = ((window as any).APP_SETTINGS?.hostname as string) || "";
      if (cloud) url = cloud.replace(/\/$/, "") + url;
    } catch (_) {
      /* keep relative — web build */
    }
  }

  // Stamp the mobile client marker on every API request. This wrapper
  // only installs on the Capacitor build, so the header is a reliable
  // "this came from the mobile app" signal the server uses to enforce
  // per-project mobile policy (Project.allow_on_mobile). Injected
  // BEFORE the queueable branch so outbox entries capture it too and
  // replays stay recognizably mobile.
  if (url.includes("/api/")) {
    const h = headersToObject(headers);
    if (!Object.keys(h).some((k) => k.toLowerCase() === "x-afri-client")) {
      h["X-Afri-Client"] = "mobile";
    }
    // Auto-attach the JWT when the caller didn't. Components written
    // for the cookie-authed web do plain ``fetch("/api/…",
    // {credentials: "same-origin"})`` — in the app there ARE no
    // cookies, so the server answered with the login PAGE, the HTML
    // guard wrapped it as a 502, and whole cards silently hid
    // ("My work tab shows empty"). The wrapper is the one place every
    // API call passes through; authenticating here fixes every
    // current and future bare call site at once.
    if (!Object.keys(h).some((k) => k.toLowerCase() === "authorization")) {
      try {
        const tok = window.localStorage.getItem("afri:jwt:access");
        if (tok) h.Authorization = `Bearer ${tok}`;
      } catch (_) {
        /* storage unavailable */
      }
    }
    headers = h;
  }

  const queueable = isWriteMethod(method) && isQueueableUrl(url);

  // Already offline? Don't even try the network — go straight to
  // the queue / IDB. Otherwise try fetch normally; if it throws
  // (network error), fall back.
  if (queueable && isOffline()) {
    const rawBodyText = await stringifyBody(body);
    // Stamp a client-generated ``unique_id`` into annotation/draft
    // POST bodies so a cloud-side replay dedupes to the same row.
    // Silent no-op for other queueable writes.
    const stamped = stampUniqueId(url, method, rawBodyText);
    const id = await enqueue({
      method,
      url,
      body: stamped.body,
      headers: headersToObject(headers),
      queuedAt: Date.now(),
      attempts: 0,
      ...(stamped.uniqueId ? { uniqueId: stamped.uniqueId } : {}),
    });
    // Mirror into IDB read stores so the offline UI sees the
    // submission immediately (DM grid, next_task heuristic) instead
    // of looping the same task back. The outbox replay will replace
    // this placeholder once the cloud is reachable.
    await mirrorAnnotationWrite(url, method, stamped.body, id);
    return synthetic202(id);
  }

  // Offline + GET on a known read pattern → serve from IDB. Same
  // ``READABLE_PATTERNS`` covers project / task / annotation /
  // draft endpoints that ``downloadProjectForOffline`` pre-populated.
  if (method === "GET" && isOffline()) {
    const idb = await tryReadFromIdb(url);
    if (idb) return idb;
  }

  // ONLINE + GET on a known read pattern → OFFLINE-FIRST: answer from
  // the local mirror IMMEDIATELY when it has the data, and revalidate
  // from the cloud in the background (the write-through tee below
  // refreshes the mirror; a change event lets the UI refetch). Before
  // this, every page load waited on the network and the mirror only
  // helped after a FAILURE — on a slow link that's the worst of both:
  // the full slow round-trip AND no benefit from local data. Reads
  // that must always be live (auth/whoami/version, anything the
  // NO_STUB set names, and explicit ``cache: "reload"`` calls such as
  // the download/prewarm path) are excluded.
  if (method === "GET" && MIRROR_FIRST && !init?.cache?.match?.(/reload|no-store/) && isMirrorFirstUrl(url)) {
    const idb = await tryReadFromIdb(url);
    if (idb) {
      let served: string | undefined;
      try {
        served = await idb.clone().text();
      } catch (_) {
        /* fine */
      }
      // An EMPTY mirror is not an answer. On a fresh device (just
      // logged in on the iPad) the projects handler returned [] and
      // mirror-first served "no projects" without ever asking the
      // network. Only short-circuit when the mirror has real content;
      // otherwise fall through to the live fetch (which tees into the
      // mirror for next time).
      if (served && !isEmptyPayload(served)) {
        revalidateInBackground(url, headers, init, served);
        idb.headers.set("X-Afri-Source", "idb-mirror-first");
        return idb;
      }
    }
  }

  // Hard-timeout the network attempt so a half-open socket to a
  // freshly-stopped cloud doesn't hang the UI. AbortController
  // wiring merges any caller-provided signal so external cancels
  // still work.
  const { signal: timeoutSignal, cancel: cancelTimeout } = withTimeout(url, init?.signal ?? null, method);
  // Build the init we pass to originalFetch with our merged signal.
  // Spread first so caller-supplied fields stick; override signal
  // last with the merged one. ``headers`` carries the client-marker
  // stamp from above (a plain object when stamped), so pass it
  // explicitly — the spread alone would resend the un-stamped ones.
  const fetchInit: RequestInit = {
    ...(init ?? {}),
    ...(headers !== undefined ? { headers } : {}),
    signal: timeoutSignal,
  };
  try {
    // Dispatch the CANONICALIZED url, not the original input — the
    // /api/ rewrite above only changed the local ``url`` variable, so
    // relative fetches still went to the LOCAL Capacitor server
    // (which answers index.html) while the headers rode along
    // correctly. String/URL inputs carry their init in fetchInit; a
    // Request object whose url we rewrote must re-supply method+body.
    const inputIsRequest = !(typeof input === "string" || input instanceof URL);
    const urlRewritten = inputIsRequest ? url !== (input as Request).url : url !== String(input);
    const dispatchInput: RequestInfo = urlRewritten ? url : (input as RequestInfo);
    const dispatchInit: RequestInit = urlRewritten && inputIsRequest ? { ...fetchInit, method, body } : fetchInit;
    const response = await originalFetch(dispatchInput, dispatchInit);
    cancelTimeout();
    // HTML answer on an API path → JSON error envelope. Checked
    // BEFORE the tee below so an HTML page can never poison the
    // offline replay cache either.
    if (url.includes("/api/")) {
      const ct = response.headers.get("content-type") || "";
      // Django stamps ``text/html`` on EMPTY responses too — a
      // successful ``204 No Content`` from a DELETE (project delete,
      // create-wizard cancel) carried that header and was turned
      // into a fake 502 "web page instead of data" → the offline
      // modal fired on every cancel/delete even though the server
      // had done the work. Only a *page* is an error: a body-less
      // 2xx/204/205/304 is a success whatever its content-type says.
      const bodyless =
        response.status === 204 ||
        response.status === 205 ||
        response.status === 304 ||
        response.headers.get("content-length") === "0";
      if (ct.includes("text/html") && !(response.ok && bodyless)) {
        return htmlToJsonEnvelope(response);
      }
    }
    // Tee responses for whitelisted endpoints into IDB on every
    // successful call. Cheap (single put) and gives us an offline
    // replay surface without changing call sites.
    if (response.ok && method === "GET") {
      const key = cacheKeyForUrl(url);
      if (key) {
        // Don't await — failure here should never delay the caller.
        persistResponse(key, response).catch(() => {});
      }
      // Phase 4 — write-through tee to the typed stores so any task,
      // project, annotation, or draft the user visits online becomes
      // reachable offline later. Complements the explicit "Download
      // for offline" flow: no user action required, just visiting the
      // task fills its cache. Also fire-and-forget.
      teeToTypedStore(url, response).catch(() => {});
    }
    return response;
  } catch (err) {
    cancelTimeout();
    // Network failure (DNS / TCP / TLS / etc.). Queue if it's a
    // queueable write; for GETs, try IDB as a last-resort read
    // fallback so a brief connection drop doesn't kill the
    // labeling session.
    if (queueable) {
      const rawBodyText = await stringifyBody(body);
      // Same idempotency stamping as the "already offline" branch —
      // if the network drops mid-request, cloud dedupes on replay.
      const stamped = stampUniqueId(url, method, rawBodyText);
      const id = await enqueue({
        method,
        url,
        body: stamped.body,
        headers: headersToObject(headers),
        queuedAt: Date.now(),
        attempts: 0,
        ...(stamped.uniqueId ? { uniqueId: stamped.uniqueId } : {}),
      });
      await mirrorAnnotationWrite(url, method, stamped.body, id);
      return synthetic202(id);
    }
    // Compute the path once for use in stubbing logic below.
    let path = url;
    try {
      if (url.startsWith("http")) path = new URL(url).pathname;
    } catch (_) {
      /* keep raw url */
    }
    const q = path.indexOf("?");
    if (q !== -1) path = path.slice(0, q);
    // Endpoints that MUST throw — used by ``useOnlineStatus`` and
    // ``AuthProvider``'s tanstack cache to detect "cloud is down".
    // Stubbing these would make the offline pill never light up.
    // EXACT match only — children of these paths (e.g. notifications
    // sub-paths under /api/current-user) should still get stubbed.
    const NO_STUB_EXACT = new Set([
      "/api/current-user/whoami",
      "/api/organizations",
      "/api/organizations/",
      "/api/version",
      "/api/version/",
      "/api/afri/auth/cloud_reachable",
      "/api/afri/auth/cloud_reachable/",
      "/api/afri/auth/state",
      "/api/afri/auth/state/",
      // Auth endpoints — MUST throw offline. Without this the
      // synthetic ``{}`` 200 stub looks like a successful login to
      // ``Login.tsx``, which then ``setTokens(undefined, undefined)``
      // and proceeds to render the *previous* user's cached identity
      // from STORE_API_CACHE. Letting the network error propagate
      // shows a clear "you must be online to sign in" toast in the
      // login form's catch handler.
      "/api/token/obtain",
      "/api/token/obtain/",
      "/api/token/refresh",
      "/api/token/refresh/",
    ]);
    const blocked = NO_STUB_EXACT.has(path);
    // For NO_STUB endpoints we usually want the network error to
    // propagate (so useOnlineStatus / tanstack notice the cloud is
    // down), BUT whoami + organizations have an IDB-cached
    // replay store. Serve from there before falling through to the
    // throw — the offline pill still lights up because the version
    // probe fails on its own, but the user keeps their org context.
    if (blocked && method === "GET") {
      const persistKey = cacheKeyForUrl(url);
      if (persistKey) {
        const replayed = await replayPersisted(persistKey);
        if (replayed) return replayed;
      }
    }
    if (!blocked) {
      // GET → IDB first, then synthetic.
      if (method === "GET") {
        const idb = await tryReadFromIdb(url);
        if (idb) return idb;
        // Persisted raw responses (notifications, dm columns, discover
        // feeds…) — replay the last-seen body before we'd ever fall to
        // an empty stub. Only the NO_STUB branch checked this before,
        // so a flaky link blanked the notification bell to "[]".
        const persistKey = cacheKeyForUrl(url);
        if (persistKey) {
          const replayed = await replayPersisted(persistKey);
          if (replayed) return replayed;
        }
        // Last-resort: URL-content cache. Populated by
        // ``downloadProjectForOffline``'s prewarm step for every
        // task.data URL. Lets LSF tags that fetch their own value
        // (RichText valueType="url", TimeSeries CSV, AudioUltra
        // bytes) keep working when cloud is unreachable. We
        // synthesize a fresh, non-opaque Response from the stored
        // blob so JS-side ``response.text()`` / XHR ``response``
        // both work — the SW media cache can't always do that for
        // cross-origin opaque entries.
        try {
          // Try the exact URL first, then a search-stripped form.
          // Tags add cache-busters (AudioUltra appends ``?lsref=1``,
          // some media re-presigns) that prewarm didn't have, so a
          // strict key lookup misses. Stripping the query falls back
          // to the prewarmed entry — which is the same bytes for the
          // same pathname.
          const stripQuery = (u: string): string => {
            const q = u.indexOf("?");
            return q === -1 ? u : u.slice(0, q);
          };
          const candidates = [url];
          const stripped = stripQuery(url);
          // Never for the cloud-storage proxy: there the query names the
          // file, and a stripped key would hand back whichever file was
          // stored first (see sw.js v12 note).
          if (stripped !== url && !/\/storage-data\//.test(url)) candidates.push(stripped);
          for (const key of candidates) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const row = await getRecord<any>(STORE_URL_CONTENT, key);
            if (row && row.body) {
              // LRU signal for the media-cap eviction (P2): stamp
              // lastUsed on every hit. Fire-and-forget — the read
              // must not wait on a write.
              putRecord(STORE_URL_CONTENT, { ...row, lastUsed: Date.now() }).catch(() => {});
              try {
                // eslint-disable-next-line no-console
                console.info("[offline-fetch] url-content-hit", key, key !== url ? "(stripped)" : "");
              } catch (_) {
                /* swallow */
              }
              return new Response(row.body, {
                status: 200,
                headers: {
                  "Content-Type": row.contentType || "application/octet-stream",
                  "X-Afri-Source": "idb-url-content",
                },
              });
            }
          }
        } catch (_) {
          /* fall through to generic stub */
        }
      }
      // Special-case: ``POST /api/dm/actions?id=next_task`` is the
      // label-stream "give me the next task" call. Returning ``{}``
      // crashes DM's ``updateItem`` because MST's TaskModel needs an
      // ``id``. Resolve from STORE_TASKS instead — pick the first
      // task for this project that doesn't have a cached annotation.
      // Same heuristic as the GET ``/api/dm/tasks/next`` IDB pattern.
      // Org switch — POST /api/organizations/switch with body
      // {organization_id: N}. Offline path: rewrite the cached
      // whoami's ``active_organization`` so the post-reload boot
      // sees the new org. Without this, the SidebarOrgHeader's
      // ``location.replace("/")`` returns the user to the same
      // pre-switch state (cached whoami still has old org), and
      // either RootPage's "no active org" branch or some
      // downstream check redirects to /auth/login.
      if (method === "POST" && (path === "/api/organizations/switch" || path === "/api/organizations/switch/")) {
        try {
          const stringified = await stringifyBody(body);
          const parsed = stringified ? JSON.parse(stringified) : {};
          const orgId = Number(parsed?.organization_id ?? 0);
          if (orgId) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const row = await getRecord<any>(STORE_API_CACHE, "/api/current-user/whoami");
            if (row?.body && typeof row.body === "object") {
              const updatedBody = { ...row.body, active_organization: orgId };
              await putRecord(STORE_API_CACHE, {
                key: "/api/current-user/whoami",
                body: updatedBody,
                savedAt: Date.now(),
              });
              // eslint-disable-next-line no-console
              console.info("[offline-fetch] org switch (offline) → cached whoami active_organization=", orgId);
              return new Response(JSON.stringify({ active_organization: orgId, queued: true }), {
                status: 200,
                headers: {
                  "Content-Type": "application/json",
                  "X-Afri-Source": "offline-stub",
                },
              });
            }
          }
        } catch (_) {
          /* fall through to generic stub */
        }
      }

      if (path === "/api/dm/actions" || path === "/api/dm/actions/") {
        try {
          const u0 = url.startsWith("http") ? new URL(url) : null;
          const qParams = u0 ? u0.searchParams : new URLSearchParams();
          const actionId = qParams.get("id") || "";
          const projectId = Number(qParams.get("project") || 0);
          if (actionId === "next_task" && projectId) {
            const tasks = await getAllByIndex<{ id: number }>(STORE_TASKS, "by_project", projectId);
            tasks.sort((a, b) => a.id - b.id);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const logPick = (t: any, why: string) => {
              if (!DEBUG_IDB_READS) return;
              try {
                const dataKeys = t?.data && typeof t.data === "object" ? Object.keys(t.data) : "no-data";
                const preview = t?.data ? JSON.stringify(t.data).slice(0, 200) : "no-data";
                // eslint-disable-next-line no-console
                console.info(
                  "[offline-fetch] dm-action next_task",
                  why,
                  "task-id=",
                  t?.id,
                  "data-keys=",
                  dataKeys,
                  "data-preview=",
                  preview,
                );
              } catch (_) {
                /* swallow */
              }
            };
            for (const t of tasks) {
              const anns = await getAllByIndex<unknown>(STORE_ANNOTATIONS, "by_task", t.id);
              if (!anns || anns.length === 0) {
                logPick(t, "first-unannotated");
                return new Response(JSON.stringify(sanitizeTask(t)), {
                  status: 200,
                  headers: {
                    "Content-Type": "application/json",
                    "X-Afri-Source": "offline-stub",
                  },
                });
              }
            }
            // No un-annotated cached task — return the first one so
            // LSF still has something to render rather than crashing.
            if (tasks[0]) {
              logPick(tasks[0], "fallback-first");
              return new Response(JSON.stringify(sanitizeTask(tasks[0])), {
                status: 200,
                headers: {
                  "Content-Type": "application/json",
                  "X-Afri-Source": "offline-stub",
                },
              });
            }
            if (DEBUG_IDB_READS) {
              // eslint-disable-next-line no-console
              console.warn("[offline-fetch] dm-action next_task: STORE_TASKS empty for project", projectId);
            }
          }
        } catch (_) {
          /* fall through to generic stub */
        }
      }
      // Only OUR API paths get a JSON stub. Anything else that failed
      // (media on storage.googleapis.com, third-party assets) must
      // propagate the real error — a fake ``{}`` 200 with an
      // application/json content-type makes an <img>/<audio> load
      // "succeed" with garbage bytes and silently break media.
      if (!path.startsWith("/api/")) throw err;
      // The empty stub exists so an OFFLINE session doesn't crash on
      // endpoints the mirror doesn't cover. When the device is
      // ONLINE and a single request merely failed/timed out (flaky
      // link, one dropped packet), a fake-empty 200 is worse than the
      // error: analytics renders "no activity", lists render empty,
      // and nothing ever retries. Propagate so the caller shows its
      // error / retry state and react-query's retry kicks in.
      if (!isOffline() && nativeConnected !== false) throw err;
      const lastSeg = path.replace(/\/+$/, "").split("/").pop() || "";
      const isList = !/^\d+$/.test(lastSeg);
      const stub = method === "GET" && isList ? "[]" : "{}";
      try {
        // eslint-disable-next-line no-console
        console.warn("[offline-fetch] stubbing", method, path);
      } catch (_) {
        /* swallow */
      }
      return new Response(stub, {
        status: 200,
        headers: {
          "Content-Type": "application/json",
          "X-Afri-Source": "offline-stub",
        },
      });
    }
    // Path is on NO_STUB_EXACT list — let the network error
    // propagate. Log it so we can confirm the right URLs are slipping
    // through (whoami / version polls / etc.).
    try {
      // eslint-disable-next-line no-console
      console.error("[offline-fetch] uncaught", method, url);
    } catch (_) {
      /* swallow */
    }
    throw err;
  }
};

/** Best-effort UUID generator. Prefers ``crypto.randomUUID`` (Chrome
 *  92+, Safari 15.4+, iOS 15.4+, all Capacitor targets we ship);
 *  falls back to a deterministic RFC-4122-shaped id for older
 *  WebViews so we never fail the enqueue path over a missing API. */
const genUuid = (): string => {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch (_) {
    /* fall through */
  }
  // RFC-4122 variant 4 shape, ~122 bits of entropy from Math.random().
  // The cloud's idempotency check is a lookup, not a security barrier
  // — this fallback is only about de-duping honest replays, not
  // resisting collision attacks.
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
};

const ANNOTATION_WRITE_RE = /\/api\/tasks\/\d+\/(annotations|drafts)\/?$/;

/** Stamp a client-generated ``unique_id`` into an annotation / draft
 *  POST body so cloud dedupes replays. Mirrors desktop's
 *  ``CloudProxyMiddleware`` semantics — see the sync architecture
 *  memory doc. Returns the possibly-modified body + the stamped uuid
 *  (or ``undefined`` when the URL isn't a queueable annotation write,
 *  or when the body already had ``unique_id`` from an earlier caller). */
const stampUniqueId = (
  url: string,
  method: string,
  bodyText: string | null,
): { body: string | null; uniqueId?: string } => {
  if (method !== "POST" || bodyText == null) return { body: bodyText };
  let path = url;
  try {
    if (url.startsWith("http")) path = new URL(url).pathname;
  } catch {
    /* ignore */
  }
  if (!ANNOTATION_WRITE_RE.test(path)) return { body: bodyText };
  try {
    const parsed = JSON.parse(bodyText);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      if (typeof parsed.unique_id === "string" && parsed.unique_id) {
        return { body: bodyText, uniqueId: parsed.unique_id };
      }
      const uniqueId = genUuid();
      parsed.unique_id = uniqueId;
      return { body: JSON.stringify(parsed), uniqueId };
    }
  } catch (_) {
    // Body wasn't JSON (e.g. serialized FormData placeholder). Skip —
    // the outbox will still replay the original bytes verbatim, just
    // without the idempotency guarantee.
  }
  return { body: bodyText };
};

/** Exponential backoff for transient replay failures. Starts at 5s
 *  after the first failure, doubles each attempt, caps at 5min so a
 *  wedged cloud can't push the retry cadence past a reasonable UX
 *  window. Adds ±20% jitter to avoid thundering-herd on reconnect. */
const backoffMs = (attempts: number): number => {
  const base = Math.min(300_000, 5_000 * 2 ** Math.max(0, attempts - 1));
  const jitter = base * 0.2 * (Math.random() - 0.5) * 2; // ±20%
  return Math.max(1_000, Math.floor(base + jitter));
};

/** Parse a ``Retry-After`` header per RFC-7231 — accepts either a
 *  delta-seconds integer or an HTTP-date. Returns the delay in ms
 *  clamped to [1s, 30min]. ``null`` when absent / malformed. */
const parseRetryAfter = (header: string | null): number | null => {
  if (!header) return null;
  const trimmed = header.trim();
  const secs = Number.parseInt(trimmed, 10);
  if (Number.isFinite(secs) && String(secs) === trimmed) {
    return Math.max(1_000, Math.min(1_800_000, secs * 1_000));
  }
  const when = Date.parse(trimmed);
  if (Number.isFinite(when)) {
    return Math.max(1_000, Math.min(1_800_000, when - Date.now()));
  }
  return null;
};

type ReplayResult =
  | { kind: "ok"; response: Response }
  | { kind: "retry"; delayMs: number }
  | { kind: "dead"; status: number; detail: string };

/** Replay one outbox entry against the network. Returns:
 *   - ``{kind: "ok"}`` on 2xx, or 409 (cloud already has this write —
 *     the ``unique_id`` / unique-constraint dedupe fired; a no-op).
 *   - ``{kind: "retry", delayMs}`` on 5xx / 401 / network error /
 *     timeout; ``delayMs`` honours the cloud's ``Retry-After`` when
 *     present, else the local exponential-backoff schedule. 401 is
 *     transient here: the replay re-reads the CURRENT access token
 *     below, so once the user signs back in the next drain succeeds.
 *   - ``{kind: "dead"}`` on any other 4xx — the cloud REJECTED the
 *     write (validation error, mobile policy, task deleted). The
 *     caller moves it to the dead-letter store; deleting it (the
 *     pre-v6 behaviour) silently lost user work. */
const replay = async (entry: OutboxEntry): Promise<ReplayResult> => {
  const nextAttempts = (entry.attempts ?? 0) + 1;
  try {
    // Refresh the Authorization header from the CURRENT token. The
    // stored headers snapshot auth at queue time; an entry queued
    // yesterday would otherwise replay with a rotated-out JWT and
    // 401 forever even though the user is signed in right now.
    const headers = { ...entry.headers };
    try {
      const token = window.localStorage?.getItem("afri:jwt:access");
      if (token && Object.keys(headers).some((k) => k.toLowerCase() === "authorization")) {
        for (const k of Object.keys(headers)) {
          if (k.toLowerCase() === "authorization") headers[k] = `Bearer ${token}`;
        }
      }
    } catch (_) {
      /* storage unavailable — replay with stored auth */
    }
    const res = await originalFetch(entry.url, {
      method: entry.method,
      headers,
      body: entry.body == null ? null : entry.body,
    });
    if (res.status >= 500 || res.status === 401) {
      const retryAfter = parseRetryAfter(res.headers.get("Retry-After"));
      return { kind: "retry", delayMs: retryAfter ?? backoffMs(nextAttempts) };
    }
    if (res.status >= 400 && res.status !== 409) {
      let detail = "";
      try {
        detail = (await res.text()).slice(0, 500);
      } catch (_) {
        /* keep empty */
      }
      return { kind: "dead", status: res.status, detail };
    }
    return { kind: "ok", response: res };
  } catch {
    return { kind: "retry", delayMs: backoffMs(nextAttempts) };
  }
};

/** After a create-POST replays successfully, rewrite the placeholder
 *  id (``-<queuedId>``, from ``synthetic202``) in every LATER queued
 *  entry to the real cloud id. Without this, an annotation created
 *  offline and then edited offline queues a PATCH against
 *  ``/api/annotations/-5/`` which can only 404 on replay.
 *  Conservative by construction: only the exact ``/annotations/-N``
 *  and ``/drafts/-N`` path segments and the exact JSON value
 *  positions ``"annotation": -N`` / ``"id": -N`` are rewritten. */
const remapPlaceholderId = async (queuedId: number, realId: number, entries: OutboxEntry[]): Promise<void> => {
  const pathRe = new RegExp(`(/api/(?:annotations|drafts)/)-${queuedId}(?=[/?]|$)`);
  const bodyRe = new RegExp(`("(?:annotation|annotation_id|id)"\\s*:\\s*)-${queuedId}(?=[,}\\s])`, "g");
  for (const later of entries) {
    let changed = false;
    let url = later.url;
    if (pathRe.test(url)) {
      url = url.replace(pathRe, `$1${realId}`);
      changed = true;
    }
    let body = later.body;
    if (body && bodyRe.test(body)) {
      bodyRe.lastIndex = 0;
      body = body.replace(bodyRe, `$1${realId}`);
      changed = true;
    }
    if (changed) {
      // Mutate the in-memory object too — the drain loop replays
      // these same array elements right after this remap runs, so
      // updating only the IDB row would replay the stale URL/body.
      later.url = url;
      later.body = body;
      await update(later);
    }
  }
};

let draining = false;

/**
 * Drain the outbox FIFO. Stops on the first entry that fails
 * transiently (5xx / network error) so order is preserved. Safe
 * to call concurrently — guarded by ``draining`` so only one
 * pass runs at a time.
 *
 * Per-entry backoff (Step 5e): after a transient failure the entry's
 * ``nextAttemptAt`` is set to ``now + backoff(attempts)`` (with
 * ``Retry-After`` honoured when the cloud sent one). Subsequent
 * drains that fire before that time short-circuit as no-ops — this
 * turns a wedged cloud from a per-tick spinner into a proper
 * exponential retry (5s → 5min cap) without changing the FIFO
 * invariant.
 */
export const drainOutbox = async (): Promise<{ ok: number; failed: number }> => {
  if (draining) return { ok: 0, failed: 0 };
  draining = true;
  let ok = 0;
  let failed = 0;
  try {
    const entries = await list();
    entries.sort((a, b) => a.queuedAt - b.queuedAt);
    const now = Date.now();
    for (let i = 0; i < entries.length; i++) {
      const entry = entries[i];
      // Entry in cooldown from a previous failure. Because we drain
      // FIFO to preserve annotation order, any queued entry AFTER
      // this one also has to wait — bail the whole pass.
      if (entry.nextAttemptAt && entry.nextAttemptAt > now) break;

      const result = await replay(entry);
      if (result.kind === "retry") {
        failed += 1;
        const nextAttempts = (entry.attempts ?? 0) + 1;
        await update({
          ...entry,
          attempts: nextAttempts,
          nextAttemptAt: Date.now() + result.delayMs,
        });
        break; // preserve order — wait for cooldown / next online tick
      }
      if (result.kind === "dead") {
        // Cloud rejected the write outright. Preserve it for review
        // instead of deleting (pre-v6 silently lost the work), then
        // CONTINUE — a rejected entry can't succeed later verbatim,
        // so it must not block the rest of the queue the way a
        // transient failure does.
        failed += 1;
        await deadLetter(entry, { status: result.status, detail: result.detail });
        try {
          window.dispatchEvent(new CustomEvent("afri:outbox-dead"));
        } catch (_) {
          /* older webviews */
        }
        continue;
      }
      // Offline-created rows: rewrite this entry's placeholder id
      // (``-<outbox id>``) to the cloud-issued id in every remaining
      // queued entry, so follow-up PATCHes land on the real row.
      if (entry.id != null && entry.method === "POST") {
        try {
          const created = await result.response.clone().json();
          const realId = Number(created?.id);
          if (Number.isFinite(realId) && realId > 0) {
            await remapPlaceholderId(entry.id, realId, entries.slice(i + 1));
          }
        } catch (_) {
          /* non-JSON response — nothing to remap */
        }
      }
      if (entry.id != null) await remove(entry.id);
      ok += 1;
      // First successful replay is incontestable proof the cloud
      // is reachable — flip ``useOnlineStatus`` to online RIGHT NOW
      // (without waiting for its next 12s probe). This is what
      // makes the offline pill disappear BEFORE the cloud-up pill
      // finishes draining: by the time the user looks back at the
      // menubar, only the upload-animation pill remains, ticking
      // its count down.
      if (ok === 1) {
        try {
          if (typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent("afri:online-confirmed"));
          }
        } catch (_) {
          /* swallow — older webviews */
        }
      }
    }
  } finally {
    draining = false;
  }
  // A successful drain changed server-side state (annotations submitted,
  // reviews recorded), so views must refetch instead of showing stale
  // cached counts until the user navigates. Without this, drained
  // offline work looked "not synced" and the Data Manager kept its old
  // totals until a home-and-back round trip. Use a DISTINCT event from
  // the frequent ``afri:mirror-updated`` (which only invalidates ACTIVE
  // queries): a drain is infrequent, so offline-first-queries
  // invalidates ALL queries on it — refetching what is mounted AND
  // marking inactive queries (e.g. a Data Manager not yet opened) stale
  // so the next mount refetches.
  if (ok > 0 && typeof window !== "undefined") {
    try {
      window.dispatchEvent(new CustomEvent("afri:outbox-drained"));
    } catch (_) {
      /* older webviews */
    }
  }
  return { ok, failed };
};

/**
 * Install the fetch wrapper + reconnect listener. Idempotent.
 * Call once at app boot from main.tsx (Capacitor only).
 *
 * Drain triggers (most → least specific):
 *   1. ``afri:online-confirmed`` — dispatched by ``useOnlineStatus``
 *      every time its 12s probe successfully reaches the cloud.
 *      This is the primary trigger on Capacitor where
 *      ``navigator.onLine`` is permanently true and the browser's
 *      ``online`` event never fires.
 *   2. Browser ``online`` event — kept for the web/desktop SPAs
 *      that DO see ``navigator.onLine`` flip.
 *   3. Periodic safety-net tick (8s) — guards against any signal we
 *      miss (event listener removed, dispatch failure, app
 *      background-resume race). Empty outbox = one ``count()`` call
 *      and we return; cheap.
 *   4. Initial best-effort drain on install — flushes anything
 *      queued from the previous session.
 *
 * No-data-loss invariant: ``drainOutbox`` is FIFO and stops on the
 * first transient failure, so a flaky connection mid-replay leaves
 * the failing entry in the queue with bumped ``attempts``. The
 * cloud's ``unique_id`` idempotency dedupes the rare double-deliver
 * (cloud accepted but response lost). Same property the desktop
 * sync engine relies on.
 */
export const installOfflineFetch = (): void => {
  if (typeof window === "undefined") return;
  if ((window as { __afri_offline_fetch_installed?: boolean }).__afri_offline_fetch_installed) return;
  if (!isCapacitor()) return;
  (window as { __afri_offline_fetch_installed?: boolean }).__afri_offline_fetch_installed = true;
  window.fetch = wrappedFetch as typeof window.fetch;

  const tryDrain = async (reason: string) => {
    try {
      const pending = await count();
      if (pending === 0) return; // nothing to do — cheap exit
      // eslint-disable-next-line no-console
      console.info("[offline-fetch] drain attempt", reason, "pending=", pending);
      const result = await drainOutbox();
      if (result.ok > 0 || result.failed > 0) {
        // eslint-disable-next-line no-console
        console.info("[offline-fetch] drain result", result);
      }
    } catch {
      /* swallow — runner state machine handles retry */
    }
  };

  // Native connectivity via @capacitor/network (no SPA import — the
  // shell injects registered plugins onto window.Capacitor.Plugins).
  // Gives isOffline() a real answer so offline writes queue INSTANTLY
  // instead of waiting out the 6s fetch timeout, and reconnection
  // triggers a drain the moment the radio comes back.
  try {
    const network = (window as any).Capacitor?.Plugins?.Network;
    if (network?.addListener) {
      network.addListener("networkStatusChange", (status: { connected: boolean }) => {
        const was = nativeConnected;
        nativeConnected = !!status?.connected;
        try {
          (window as any).__afri_native_connected = nativeConnected;
        } catch (_) {
          /* ignore */
        }
        if (was === false && nativeConnected) void tryDrain("network-restored");
        try {
          window.dispatchEvent(new CustomEvent("afri:native-network", { detail: { connected: nativeConnected } }));
        } catch (_) {
          /* older webviews */
        }
      });
      network
        .getStatus?.()
        .then((s: { connected: boolean }) => {
          nativeConnected = !!s?.connected;
          try {
            (window as any).__afri_native_connected = nativeConnected;
          } catch (_) {
            /* ignore */
          }
          // Announce the INITIAL state too — an offline cold start
          // must flip the connectivity indicator immediately, not wait
          // for the probe to fail twice.
          try {
            window.dispatchEvent(new CustomEvent("afri:native-network", { detail: { connected: nativeConnected } }));
          } catch (_) {
            /* older webviews */
          }
        })
        .catch(() => {
          /* keep null — unknown */
        });
    }
  } catch (_) {
    /* plugin absent — fetch timeout remains the detector */
  }

  // Cloud-reachability event from useOnlineStatus.
  window.addEventListener("afri:online-confirmed", () => {
    void tryDrain("online-confirmed");
  });
  // Browser-level online event (web/desktop SPAs).
  window.addEventListener("online", () => {
    void tryDrain("browser-online");
  });
  // Periodic safety-net. 8s ticks survive any missed signal —
  // worst case the user waits ~8s after reconnect for a Submit to
  // appear cloud-side. Cheap when outbox is empty (one IDB count).
  window.setInterval(() => {
    void tryDrain("interval-tick");
  }, 8000);
  // Initial drain — handles outbox entries from a previous app
  // session. ``setTimeout(0)`` defers past first render so we
  // don't compete with React for IDB.
  window.setTimeout(() => {
    void tryDrain("install");
  }, 0);

  // Mirror warm-up (P5). Mirror-first reads only help once the mirror
  // has rows; a fresh install after login has none until the user
  // visits or downloads something. Prefetch the project list + the
  // first page of each mobile-enabled project's tasks in the
  // background ~10s after boot (idle, online, signed in), so the very
  // next navigation paints from the mirror. Cheap: a few small GETs
  // through the same tee that fills the mirror on any normal visit.
  const warmMirror = async () => {
    try {
      if (isOffline()) return;
      const token = window.localStorage?.getItem("afri:jwt:access");
      if (!token) return;
      const host = ((window as any).APP_SETTINGS?.hostname as string) || "";
      const H = { Authorization: `Bearer ${token}`, "X-Afri-Client": "mobile" };
      const r = await originalFetch(
        `${host}/api/projects/?page_size=30&include=id,title,allow_on_mobile,task_number,finished_task_number,color,logo,updated_at,created_at`,
        { headers: H, cache: "no-store" },
      );
      if (!r.ok) return;
      await teeToTypedStore(`${host}/api/projects/`, r.clone());
      const body = await r.json();
      const projects: Array<{ id: number }> = Array.isArray(body) ? body : (body?.results ?? []);
      for (const p of projects.slice(0, 5)) {
        try {
          const t = await originalFetch(`${host}/api/projects/${p.id}/tasks/?page=1&page_size=50`, {
            headers: H,
            cache: "no-store",
          });
          if (t.ok) await teeToTypedStore(`${host}/api/projects/${p.id}/tasks/`, t.clone());
        } catch (_) {
          /* best-effort per project */
        }
      }
    } catch (_) {
      /* warm-up is opportunistic */
    }
  };
  let warmed = false;
  const warmOnce = () => {
    if (warmed) return;
    warmed = true;
    void warmMirror();
  };
  window.setTimeout(warmOnce, 10_000);
  // Re-warm ONLY on the offline→online edge (not on every connectivity
  // event) so a long offline stretch ends with a fresh mirror.
  let prevConnected = true;
  window.addEventListener("afri:native-network", (e: Event) => {
    const d = (e as CustomEvent<{ connected?: boolean }>).detail;
    const now = !!d?.connected;
    if (now && !prevConnected) {
      warmed = false;
      window.setTimeout(warmOnce, 3_000);
    }
    prevConnected = now;
  });
};

/** Public hook for UI: how many writes are pending sync. */
export const pendingCount = (): Promise<number> => count();
